<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { STATUS_OPEN, STATUS_CLOSED, AIRPORT, REGION, SUB_REGION, AIR_TYPE, HOTEL_TYPE, PACKAGE_TYPE, TOUR_TYPE, CRUISE_TYPE } from '@/constants';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator, FilterService } from '@primevue/core/api';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import ActionButtons from '@/components/ActionButtons.vue';
import DeleteButton from '@/components/DeleteButton.vue';
import TypeView from './TypeView.vue';

interface Props {
  city?: any[];
  urlUpload?: string;
  isView?: boolean;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  handleEditCity: [city: any];
  handleDeleteCity: [city: any];
  handlePreview: [data: any];
  handleShowRevertVersion: [];
}>();

const { t } = useI18n({ useScope: 'global' });
const { styleColumnExport, formatDataExport, renderNameFileExport } = useViewComposable();
const widthList = [10, 40, 40, 20, 20, 20, 20, 20, 20, 15, 10, 10, 40, 10, 40, 10, 10, 10, 10];
const sheetColumnStyle = styleColumnExport(widthList);

const filters = ref();
const globalFilterValue = ref();
const sheetsData = ref<any>([]);
const sortOrder = ref(0);
const sortField = ref('validateMessage');
const isSort = ref(false);
const dataTableCityRef = ref();

const columnFields = ref([
  { field: 'airportCode', header: t('management.air.airport_code') },
  { field: 'airportNameEn', header: t('management.air.airport_name') },
  { field: 'airportNameFr', header: t('management.air.airport_name_french') },
  { field: 'cityNameEn', header: t('management.air.city_name') },
  { field: 'cityNameFr', header: t('management.air.city_name_french') },
  { field: 'regionEn', header: t('management.air.region') },
  { field: 'regionFr', header: t('management.air.region_french') },
  { field: 'countryNameEn', header: t('management.air.country_name') },
  { field: 'countryNameFr', header: t('management.air.country_name_french') },
  { field: 'destSvId', header: t('management.air.sv_dest_id') },
  { field: 'parentDestId', header: t('management.air.parent_dest_id') },
  { field: 'gatewayStatus', header: t('management.air.gateway_status') },
  { field: 'gatewayApplicability', header: t('management.air.gateway_applicability') },
  { field: 'destinationStatus', header: t('management.air.destination_status') },
  { field: 'destinationApplicability', header: t('management.air.destination_applicability') },
  { field: 'referenceType', header: t('management.package.type') },
  { field: 'brochure', header: t('management.package.brochure') },
  { field: 'deleteStatus', header: t('management.tour.deleted_status') },
  { field: 'action', header: t('common.action') }
]);

const formatFieldColumnExport = (isDisplayColumn?: boolean) => {
  const filterColumn = columnFields.value.filter((column: any) => column.field !== 'action' && column.field !== 'deleteStatus');
  return !isDisplayColumn ? filterColumn : [...filterColumn, { field: 'validateMessage', header: t('management.hotel.message') }];
};
const columns = ref(props.isView ? formatFieldColumnExport(true) : columnFields.value);

const globalFilterFields = ref([
  'airportCode',
  'airportNameEn',
  'airportNameFr',
  'cityNameEn',
  'cityNameFr',
  'regionEn',
  'regionFr',
  'countryNameEn',
  'countryNameFr',
  'destSvId',
  'parentDestId',
  'referenceType',
  'gatewayStatus',
  'destinationStatus',
  'deleteStatus',
  'gatewayApplicability',
  'brochure',
  'destinationApplicability'
]);
const rowsPerPageOptions = ref([10, 25, 50, 100, 200, 400, 500]);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);
const referenceTypeList = ref<any>([
  {
    label: t('common.airport'),
    value: AIRPORT
  },
  {
    label: t('common.region'),
    value: REGION
  },
  {
    label: t('common.sub_region'),
    value: SUB_REGION
  }
]);

const typeApplicability = ref([
  {
    label: t('common.package'),
    value: PACKAGE_TYPE
  },
  {
    label: t('common.air'),
    value: AIR_TYPE
  },
  {
    label: t('common.hotel'),
    value: HOTEL_TYPE
  },
  {
    label: t('common.cruise'),
    value: CRUISE_TYPE
  },
  {
    label: t('common.tour'),
    value: TOUR_TYPE
  }
]);

const recordStatus = ref([
  {
    label: t('common.none'),
    value: false
  },
  {
    label: t('common.deleted'),
    value: true
  }
]);

FilterService.register('filterByArrayEquals', (typeOption, filterValue) => {
  const filterOption = convertToArrayType(typeOption);
  return !filterValue || filterValue.every((val: any) => filterOption.includes(val));
});

const initFilters = () => {
  filters.value = {
    global: { value: globalFilterValue.value, matchMode: FilterMatchMode.CONTAINS },
    airportNameEn: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    airportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cityNameEn: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cityNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryNameEn: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    countryNameFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gatewayStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    regionEn: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    regionFr: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destSvId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    parentDestId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    referenceType: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    deleteStatus: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }] },
    brochure: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    gatewayApplicability: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterByArrayEquals' }] },
    destinationApplicability: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: 'filterByArrayEquals' }] }
  };
};
initFilters();

const clearFilter = () => {
  globalFilterValue.value = null;
  initFilters();
};

const filterQuote = useDebounceFn(() => {
  filters.value['global'].value = globalFilterValue.value;
}, 400);

const getSeverity = (currentStatus: string) => {
  switch (currentStatus) {
    case STATUS_CLOSED:
      return 'danger';
    case STATUS_OPEN:
      return 'info';
    default:
      return '';
  }
};

const convertToArrayType = (typeApplication: any) => {
  const optionType = [PACKAGE_TYPE, AIR_TYPE, HOTEL_TYPE, TOUR_TYPE, CRUISE_TYPE];
  const newType = optionType.filter((_item, index) => Object.values(typeApplication)[index]);
  return newType;
};

const isFilterTypeApplication = (field: string) => {
  const fields = ['gatewayApplicability', 'destinationApplicability'];
  return fields.includes(field);
};

const handleDataExport = (listData: any) => {
  return listData.map((data: any) => {
    return {
      ...data,
      gatewayApplicability: convertToArrayType(data.gatewayApplicability).join('|'),
      destinationApplicability: convertToArrayType(data.destinationApplicability).join('|')
    };
  });
};

const handleShowMessage = (message: string) => {
  return message ?? t('common.labels.success');
};

const rowClass = (data: any) => {
  return [{ 'bg-error': data?.validateMessage }];
};

const sortMessage = () => {
  isSort.value = !isSort.value;
  sortOrder.value = isSort.value ? -1 : 0;
};

const showPreviewImport = ({ data, file }: any) => {
  emits('handlePreview', {
    city: data,
    file,
    type: 'IMPORT'
  });
};

const handleShowRevertVersion = () => {
  emits('handleShowRevertVersion');
};

const editCity = (city: any) => {
  emits('handleEditCity', city);
};

const deleteCity = (city: any) => {
  emits('handleDeleteCity', city);
};

watch(
  () => props.city,
  (newVal: any) => {
    sheetsData.value = [
      {
        name: 'Sheet 1',
        data: formatDataExport(handleDataExport(newVal), formatFieldColumnExport())
      }
    ];
    frozenTableCity();
  },
  { deep: true }
);

// Temporary fix due to issue in PrimeVue 4.2.5 for frozen columns
// https://github.com/primefaces/primevue/issues/6067
// To be removed as soon as we have a fix
const frozenTableCity = () => {
  const tableTours = dataTableCityRef.value?.$el.querySelector('.p-datatable-thead');
  if (tableTours) {
    const colWidths = tableTours.querySelectorAll('th');
    document.documentElement.style.setProperty('--city-width-1', 0 + 'px');
    document.documentElement.style.setProperty('--city-width-2', +colWidths[0].offsetWidth + 'px');
    document.documentElement.style.setProperty('--city-width-3', 0.3 + colWidths[0].offsetWidth + colWidths[1].offsetWidth + 'px');
  }
};

defineExpose({
  handleDataExport,
  formatFieldColumnExport,
  sheetColumnStyle
});
</script>
<template>
  <div class="city-table">
    <DataTable
      v-model:filters="filters"
      :value="city"
      ref="dataTableCityRef"
      paginator
      showGridlines
      :rows="rowsPerPageOptions[0]"
      :rowsPerPageOptions="rowsPerPageOptions"
      filterDisplay="menu"
      scrollHeight="900px"
      :sortOrder="sortOrder"
      :sortField="sortField"
      scrollable
      :class="[{ 'is-view': props.isView }]"
      :rowClass="rowClass"
      :globalFilterFields="globalFilterFields"
    >
      <template #header>
        <div class="flex justify-content-between">
          <ActionButtons
            @clearFilter="clearFilter"
            :exportRole="!isView"
            :importRole="!isView"
            :revertRole="!isView"
            :sheetColumnStyle="sheetColumnStyle"
            :sheetsData="sheetsData"
            :fileName="renderNameFileExport('Gateway/Destination/Region_Reference')"
            :importLabel="$t('management.upload_gate_des_region_refer')"
            :urlUpload="urlUpload"
            @onApply="(data) => showPreviewImport(data)"
            @showRevertVersion="handleShowRevertVersion()"
          />
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search" />
              <InputText id="search-input" v-model="globalFilterValue" @input="filterQuote" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <TableColumn
        :show-filter-operator="false"
        :showFilterMatchModes="!(isFilterTypeApplication(column.field) || column.field === 'deleteStatus')"
        :show-add-button="false"
        v-for="(column, indexColumn) of columns"
        :key="column.field"
        :field="column.field"
        :sortable="column.field === 'validateMessage'"
        :frozen="indexColumn <= 2"
      >
        <template #header>
          <span>
            {{ column.header }}
          </span>
        </template>
        <template #body="{ data, field }">
          <span v-if="field === 'gatewayStatus' || field === 'destinationStatus'">
            <ToggleSwitch id="dest-status" v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" disabled />
          </span>
          <span v-else-if="isFilterTypeApplication(field)">
            <TypeView :applicability="data[field]" />
          </span>
          <span v-else-if="field === 'referenceType'">
            <TypeView :referenceType="data[field]" isReferenceType />
          </span>
          <span v-else-if="field === 'deleteStatus'">
            <div class="flex flex-col">
              <span> {{ data[field] ? $t('management.tour.deleted') : '' }} </span>
            </div>
          </span>
          <span v-else-if="field === 'validateMessage'">
            <div class="min-w-200">
              <p class="mb-0 break-spaces">{{ handleShowMessage(data[field]) }}</p>
            </div>
          </span>
          <span v-else-if="field === 'action'">
            <div class="flex">
              <Button id="edit-btn" icon="pi pi-pencil" class="p-button-text" @click="editCity(data)" />
              <DeleteButton :record="data" isCityModal icon="pi pi-trash" @handleDelete="(city) => deleteCity(city)" />
            </div>
          </span>
          <span v-else>
            {{ data[field] }}
          </span>
        </template>
        <template #sorticon>
          <i class="ml-2 sort-icon" :class="sortOrder === -1 ? 'pi pi-sort-amount-down-alt' : 'pi pi-sort-amount-up-alt'" @click="sortMessage()"></i>
        </template>
        <template #filter="{ filterModel, field }" v-if="globalFilterFields.includes(column.field)">
          <span v-if="field === 'gatewayStatus' || field === 'destinationStatus'">
            <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
              <template #option="slotProps">
                <Tag :value="slotProps.option" :severity="getSeverity(slotProps.option)" />
              </template>
            </Select>
          </span>
          <span v-else-if="isFilterTypeApplication(field)">
            <MultiSelect display="chip" v-model="filterModel.value" :placeholder="$t('rule_history_log.city.select_applicability')" :options="typeApplicability" optionLabel="label" optionValue="value" class="w-full"> </MultiSelect>
          </span>
          <span v-else-if="field === 'referenceType' || field === 'deleteStatus'">
            <Select v-model="filterModel.value" :options="field === 'referenceType' ? referenceTypeList : recordStatus" optionLabel="label" optionValue="value" :placeholder="$t('common.select_one')" showClear />
          </span>
          <span v-else>
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="column.header" />
          </span> </template
      ></TableColumn>
    </DataTable>
  </div>
</template>
